import { Link, matchPath, Outlet, useLocation } from "react-router-dom";
import { Home } from "lucide-react";
import { useMediaQuery } from "usehooks-ts";
import { useEffect, useState } from "react";
import { Button } from "@heroui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";

import "aos/dist/aos.css";
import { AppSidebar } from "../components/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../components/ui/sidebar";

import NavbarTop from "./Navbar";
import Notifications from "@/features/ui/Notifications";
import { AuthStore } from "@/store/useAuthStore.ts";
import { SidebarRight } from "@/components/sidebar-right";
import InboxRequests from "@/features/Notifications/Inbox";
import { useNotifications } from "@/hooks/useNotifications.ts";
import { NotificationList } from "@/features/Notifications/notification-list.tsx";
import { RequestList } from "@/features/Notifications/RequestList.tsx";
import { useRealTimeListeners } from "@/hooks/useRealTimeListeners.tsx";

import AOS from "aos";
import AuthPage from "@/pages/Auth.Page.tsx";
import logo from "/public/logo.png";
import SGE2 from "/public/SGE2.png";
import PoweredBy from "@/PoweredBy.tsx";

export default function Layout() {
  const { notifications } = AuthStore();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const location = useLocation();

  const notificationsList = useNotifications();
  // Keep animal data synchronized for farmer, supervisor, and admin sessions.
  useRealTimeListeners();

  useEffect(() => {
    AOS.init({
      once: true,
      disable: "phone",
      duration: 600,
      easing: "ease-out-sine",
    });
  });

  const isMobile = useMediaQuery("(max-width: 768px)");
  const landing =
    "bg-hero-pattern flex flex-col" +
    " overflow-y-auto" +
    " supports-[overflow:clip]:overflow-clip font-inter" +
    " text-base" +
    " text-gray-200 antialiased";
  const otherPages =
    "flex flex-col overflow-y-auto" + " supports-[overflow:clip]:overflow-clip";

  if (
    location.pathname !== "/admin" &&
    location.pathname !== "/home" &&
    location.pathname !== "/add-animal" &&
    location.pathname !== "/overview" &&
    location.pathname !== "/supervisor/dashboard" &&
    location.pathname !== "/admin/supervisor" &&
    location.pathname !== "/settings" &&
    location.pathname !== "/analytics" &&
    location.pathname !== "/admin/admin-add-animal" &&
    location.pathname !== "/admin/recipient-analysis-form" &&
    location.pathname !== "/animals/id" &&
    location.pathname !== "/guest/dashboard" &&
    !matchPath(
      {
        path: "/animals/:id",
        end: true,
      },
      location.pathname,
    ) &&
    !matchPath(
      {
        path: "/admin/recipient-analysis-form/:id",
        end: true,
      },
      location.pathname,
    ) &&
    !matchPath(
      {
        path: "/admin/*",
        end: true,
      },
      location.pathname,
    ) &&
    !matchPath(
      {
        path: "/admin/animals/:id",
        end: true,
      },
      location.pathname,
    ) &&
    !matchPath(
      {
        path: "/farmers-animals/animal/profile/:id",
        end: true,
      },
      location.pathname,
    )
  ) {
    return (
      <div
        className={
          location.pathname === "/" ||
          location.pathname === "/login" ||
          location.pathname === "/register" ||
          location.pathname === "/forgotPassword" ||
          location.pathname === "/forgotPassword/" ||
          location.pathname.startsWith("/resetPassword") ||
          location.pathname === "/look-up"
            ? `${landing}`
            : `${otherPages}`
        }
      >
        {/*<Header />*/}
        <div className="relative flex flex-col h-full ">
          <header className=" w-full">
            <NavbarTop />
          </header>
          {location.pathname === "/" && (
            <main className="h-screen w-screen overflow-y-hidden mx-auto flex-grow ">
              <AuthPage />
            </main>
          )}
          {location.pathname !== "/" && (
            <main
              className={
                "h-full dark:border-gray-800 overflow-y-auto  mx-auto" +
                "   flex-grow" +
                " pt-2"
              }
            >
              <Outlet />
            </main>
          )}
        </div>
      </div>
    );
  } else {
    return (
      <>
        <SidebarProvider
          className={"w-full" + " overflow-x-hidden dark:bg-stone-950"}
          defaultOpen={false}
        >
          <AppSidebar />
          <SidebarInset>
            <header
              className="w-screen bg-gray-100 z-50 dark:bg-stone-950 overflow-x-hidden flex h-16 shrink-0 items-center gap-2 transition-[width,height]
                              ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12
                              border-b border-gray-100 dark:border-b dark:border-gray-900 px-2"
            >
              <div className="w-full flex flex-row justify-between items-center gap-2 px-2">
                <SidebarTrigger
                  className="-ml-1"
                  setShowNotifications={setShowNotifications}
                  setShowRequests={setShowRequests}
                />
                <div className="flex flex-col items-center justify-center">
                  <img
                    alt="Logo"
                    height="200"
                    width="200"
                    className="z-20 absolute left-10 hidden sm:block"
                    src={logo}
                  />
                  {/* <img */}
                  {/*   alt="Logo" */}
                  {/*   className="h-8 z-20 absolute left-10 block sm:hidden" */}
                  {/*   src={logoSmall} */}
                  {/* /> */}
                </div>

                {location.pathname.startsWith("/admin") ? (
                  <>
                    <div className={"flex flex-row gap-2"}>
                      <Link to="/admin">
                        <Button
                          color="secondary"
                          aria-label={"Go to Admin Dashboard"}
                          title="Go to Admin Dashboard"
                          endContent={<Home size={16} />}
                          href="/admin"
                          size="sm"
                          variant="flat"
                        />
                      </Link>
                    </div>
                    <div
                      className={
                        "flex flex-row justify-center items-center gap-2" +
                        "  lg:mr-10"
                      }
                    >
                      <Notifications
                        notifications={notifications.unread}
                        setShowNotifications={setShowNotifications}
                        setShowRequests={setShowRequests}
                        showNotifications={showNotifications}
                        onOpen={onOpen}
                      />
                      <InboxRequests
                        requests={notifications.pendingRequests}
                        setShowNotifications={setShowNotifications}
                        setShowRequests={setShowRequests}
                        showRequests={showRequests}
                        onOpen={onOpen}
                      />
                    </div>
                  </>
                ) : (
                  <></>
                  // <DashboardHeader />
                )}
              </div>
            </header>
            {/* <TracingBeam className="h-full w-screen pt-2"> */}
            <div className="flex flex-1 w-full">
              <main
                className={`flex-1  ${
                  showNotifications && isMobile
                    ? "w-screen"
                    : " w-full" + "" || (showRequests && isMobile)
                      ? "w-screen "
                      : " w-full" + ""
                } overflow-x-auto ${showNotifications ? "lg:pr-10" : "lg:pr-10"} ${
                  showRequests ? "lg:pr-2" : "lg:pr-10"
                }`}
              >
                {/* <div className="hero-background absolute inset-0 bg-gradient-to-br from-yellow-50 via-blue-50 to-green-100 z-[-0]"> */}
                {/*   <div className="absolute inset-0 z-[-0] opacity-40"> */}
                {/*     <div */}
                {/*       className="absolute inset-0 z-[-0]" */}
                {/*       style={{ */}
                {/*         backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%239C92AC' fillOpacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`, */}
                {/*       }} */}
                {/*     /> */}
                {/*   </div> */}
                {/* </div> */}

                {/* <HeroBackground /> */}
                <Outlet />
                <div className="flex-1 rounded-xl md:min-h-min" />
                <PoweredBy
                  companyName={"Sila Genetics"}
                  logoAlt={"company logo"}
                  logoSrc={SGE2}
                  size={"sm"}
                />
              </main>
              {showNotifications && !isMobile && (
                <div className="w-[40%]">
                  <SidebarRight
                    showNotifications={showNotifications}
                    showRequests={showRequests}
                  />
                </div>
              )}
              {showRequests && !isMobile && (
                <div className="w-[40%]">
                  <SidebarRight
                    showNotifications={showNotifications}
                    showRequests={showRequests}
                  />
                </div>
              )}
            </div>
            {/* </TracingBeam> */}
          </SidebarInset>
        </SidebarProvider>
        {(showNotifications && isMobile && !showRequests && (
          <Modal
            isOpen={isOpen}
            placement="center"
            scrollBehavior={"inside"}
            size="lg"
            onClose={() => setShowNotifications(false)}
            onOpenChange={onOpenChange}
          >
            <ModalContent>
              {() => (
                <>
                  <ModalHeader className="flex flex-col gap-1 border-b-2 bg-blue-500 border-b-gray-700 text-gray-200">
                    Notifications
                  </ModalHeader>
                  <ModalBody>
                    <NotificationList notifications={notificationsList.data} />
                  </ModalBody>
                </>
              )}
            </ModalContent>
          </Modal>
        )) ||
          (showRequests && isMobile && !showNotifications && (
            <Modal
              draggable={true}
              isOpen={isOpen}
              placement="center"
              scrollBehavior={"inside"}
              size="lg"
              onClose={() => setShowRequests(false)}
              onOpenChange={onOpenChange}
            >
              <ModalContent>
                {(onClose) => (
                  <>
                    <ModalHeader className="flex flex-col gap-1 border-b-2 bg-blue-500 border-b-gray-700 text-gray-200">
                      Requests
                    </ModalHeader>
                    <ModalBody>
                      <RequestList
                        setShowRequests={setShowRequests}
                        onClose={onClose}
                      />
                    </ModalBody>
                  </>
                )}
              </ModalContent>
            </Modal>
          ))}
      </>
    );
  }
}
